/**
 * HeadlessEngineContext — a concrete, in-memory {@link IEngineContext} with no rendering
 * or native dependencies.
 *
 * It backs the capability surface with plain maps so executors (TS behaviours, the graph
 * interpreter) can be exercised end-to-end in unit tests and in headless/CLI runs, and it
 * serves as the reference implementation the player/editor concrete contexts mirror. The
 * contract is identical; only the backing differs (here: maps; player: SoA columns + input
 * + native physics).
 */

import type {
  ApiVersion,
  ComponentToken,
  EntityHandle,
  IEngineContext,
  LogCaps,
  SpriteCaps,
  TimeCaps,
  TransformCaps,
  Unsubscribe,
  Vec2Like,
  WorldCaps,
} from "./IEngineContext";
import { NO_ENTITY } from "./IEngineContext";
import type { EngineApi } from "./EngineApi.generated";
import { buildEngineApi } from "./ScriptApiRegistry";
import { isEngineComponent } from "./EngineComponent";
import type { PhysicsBacking } from "./modules/PhysicsScriptApi";

export const HEADLESS_API_VERSION: ApiVersion = { major: 0, minor: 1 };

interface TransformRecord {
  x: number;
  y: number;
  z: number;
  /** Rotation in degrees (matches the runtime Transform convention). */
  rotation: number;
  sx: number;
  sy: number;
}

interface SpriteRecord {
  rgb: number;
  alpha: number;
}

interface EntityRecord {
  transform: TransformRecord;
  sprite: SpriteRecord;
  components: Map<string, unknown>;
}

/**
 * An in-memory world: entity records + frame timing + input + an event bus. Drives the
 * capability implementations handed to executors via {@link HeadlessEngineContext}.
 */
export class HeadlessWorld {
  private _entities = new Map<EntityHandle, EntityRecord>();
  private _byGuid = new Map<string, EntityHandle>();
  private _nextId = 1;

  time = 0;
  deltaTime = 0;
  fixedDeltaTime = 1 / 60;

  /**
   * Physics backing for `engine.physics` (no real solver headless). Defaults to a no-op;
   * tests may replace it to exercise query/force dispatch through the engine-API surface.
   */
  physics: PhysicsBacking = { raycast: () => null, overlapCircle: () => [] };

  private _inputAxes = new Map<string, number>();
  private _inputPressed = new Set<string>();
  private _inputDown = new Set<string>();
  private _inputUp = new Set<string>();

  private _events = new Map<string, Set<(p?: Record<string, unknown>) => void>>();

  spawn(guid?: string): EntityHandle {
    const id = this._nextId++;
    this._entities.set(id, {
      transform: { x: 0, y: 0, z: 0, rotation: 0, sx: 1, sy: 1 },
      sprite: { rgb: 0xffffff, alpha: 1 },
      components: new Map(),
    });
    if (guid) this._byGuid.set(guid, id);
    return id;
  }

  destroy(entity: EntityHandle): void {
    this._entities.delete(entity);
    for (const [g, id] of this._byGuid) if (id === entity) this._byGuid.delete(g);
  }

  /** Resolve a GUID registered at spawn to its entity handle (or `null`). */
  entityByGuid(guid: string): EntityHandle | null {
    return this._byGuid.get(guid) ?? null;
  }

  has(entity: EntityHandle): boolean {
    return this._entities.has(entity);
  }

  record(entity: EntityHandle): EntityRecord {
    const r = this._entities.get(entity);
    if (!r) throw new Error(`[HeadlessWorld] unknown entity ${entity}`);
    return r;
  }

  // --- input control (for tests / headless drivers) ---
  setAxis(action: string, value: number): void {
    this._inputAxes.set(action, value);
  }
  setPressed(action: string, pressed: boolean): void {
    if (pressed) this._inputPressed.add(action);
    else this._inputPressed.delete(action);
  }
  axis(action: string): number {
    return this._inputAxes.get(action) ?? 0;
  }
  pressed(action: string): boolean {
    return this._inputPressed.has(action);
  }
  down(action: string): boolean {
    return this._inputDown.has(action);
  }
  up(action: string): boolean {
    return this._inputUp.has(action);
  }

  // --- events ---
  on(eventId: string, handler: (p?: Record<string, unknown>) => void): Unsubscribe {
    let set = this._events.get(eventId);
    if (!set) {
      set = new Set();
      this._events.set(eventId, set);
    }
    set.add(handler);
    return () => set!.delete(handler);
  }
  emit(eventId: string, payload?: Record<string, unknown>): void {
    this._events.get(eventId)?.forEach((h) => h(payload));
  }

  /** Construct a capability-scoped context bound to `entity`. */
  contextFor(entity: EntityHandle): IEngineContext {
    return new HeadlessEngineContext(this, entity);
  }
}

export class HeadlessEngineContext implements IEngineContext {
  readonly apiVersion = HEADLESS_API_VERSION;
  readonly entity: EntityHandle;

  readonly engine: EngineApi;
  readonly world: WorldCaps;
  readonly time: TimeCaps;
  readonly log: LogCaps;
  readonly transform: TransformCaps;
  readonly sprite: SpriteCaps;

  constructor(private readonly _world: HeadlessWorld, entity: EntityHandle = NO_ENTITY) {
    this.entity = entity;
    const w = _world;
    const self = this;

    this.time = {
      get time() {
        return w.time;
      },
      get deltaTime() {
        return w.deltaTime;
      },
      get fixedDeltaTime() {
        return w.fixedDeltaTime;
      },
    };

    this.log = {
      log: (...a) => console.log("[script]", ...a),
      warn: (...a) => console.warn("[script]", ...a),
      error: (...a) => console.error("[script]", ...a),
    };

    this.world = {
      spawn: () => w.spawn(),
      destroy: (e) => w.destroy(e),
      addComponent: <T>(e: EntityHandle, token: ComponentToken<T>): T => {
        const data = {} as T;
        w.record(e).components.set(token.typeId, data);
        return data;
      },
      removeComponent: (e, token) => w.record(e).components.delete(token.typeId),
      getComponent: <T>(e: EntityHandle, token: ComponentToken<T>): T | null => {
        const c = (w.record(e).components.get(token.typeId) as T) ?? null;
        if (isEngineComponent(c)) c.__bindView(e, self.engine);
        return c;
      },
      hasComponent: (e, token) => w.record(e).components.has(token.typeId),
      query: function* (...tokens) {
        // Headless: linear scan is fine.
        for (let id = 1; w.has(id); id++) {
          const rec = w.record(id);
          if (tokens.every((t) => rec.components.has(t.typeId))) yield id;
        }
      },
      getEntityByGuid: (guid) => w.entityByGuid(guid),
      getTransform: (e) => {
        if (!w.has(e)) return null;
        const r = w.record(e).transform;
        return { position: { x: r.x, y: r.y }, rotation: r.rotation, scale: { x: r.sx, y: r.sy } };
      },
      setPosition: (e, x, y) => {
        if (!w.has(e)) return;
        const r = w.record(e).transform;
        r.x = x; r.y = y;
      },
      setRotation: (e, deg) => {
        if (!w.has(e)) return;
        w.record(e).transform.rotation = deg;
      },
      setScale: (e, sx, sy) => {
        if (!w.has(e)) return;
        const r = w.record(e).transform;
        r.sx = sx; r.sy = sy;
      },
    };

    const t = (): TransformRecord => w.record(self.entity).transform;
    this.transform = {
      getPosition: (): Vec2Like => ({ x: t().x, y: t().y }),
      setPosition: (x, y) => {
        const r = t();
        r.x = x;
        r.y = y;
      },
      translate: (dx, dy) => {
        const r = t();
        r.x += dx;
        r.y += dy;
      },
      getRotation: () => t().rotation,
      setRotation: (deg) => {
        t().rotation = deg;
      },
      rotate: (d) => {
        t().rotation += d;
      },
      getScale: (): Vec2Like => ({ x: t().sx, y: t().sy }),
      setScale: (x, y) => {
        const r = t();
        r.sx = x;
        r.sy = y;
      },
    };

    const sp = (): SpriteRecord => w.record(self.entity).sprite;
    this.sprite = {
      setColor: (rgb) => {
        sp().rgb = rgb;
      },
      setAlpha: (a) => {
        sp().alpha = a;
      },
    };

    // Global-services surface (§2/§6) backed by the in-memory world: physics is the world's
    // (settable) backing, events its bus, scene its spawn/destroy.
    this.engine = buildEngineApi({
      physics: w.physics,
      events: { emit: (id, p) => w.emit(id, p), on: (id, h) => w.on(id, h) },
      input: {
        getInput: (a) => w.axis(a),
        isInputPressed: (a) => w.pressed(a),
        isInputDown: (a) => w.down(a),
        isInputUp: (a) => w.up(a),
      },
      scene: { spawn: () => w.spawn(), destroy: (e) => w.destroy(e) },
    });
  }
}
